import React, { useCallback, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { es as esLocale, enGB as enLocale } from "date-fns/locale";
import { Icon } from "../icons/Icon";
import Label from "../label/Label";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { useLanguage } from "@/context/language-context/LanguageContext";
import "react-day-picker/dist/style.css";
import "./calendar.css";
import { cn } from "@/lib/styles.utils";

type Props = {
  value?: Date | null;
  onChange?: (d: Date | null) => void;
  disabled?: boolean;
  name: string;
  className?: string;
  label?: string;
  required?: boolean;
  size?: "large" | "medium";
  helpText?: string;
  warning?: boolean;
  warningMessage?: string;
  error?: boolean;
  errorMessage?: string;
  success?: boolean;
  successMessage?: string;
  readOnly?: boolean;
  noBottomSpace?: boolean;
};

const InputCalendar = ({
  value = null,
  onChange,
  disabled,
  required,
  name,
  className = "",
  label,
  size = "medium",
  helpText,
  warning,
  warningMessage,
  error,
  errorMessage,
  success,
  successMessage,
  readOnly,
  noBottomSpace,
}: Props) => {
  const { language } = useLanguage();
  const translate = useTranslate();
  const openRef = React.useRef<HTMLButtonElement>(null);
  const [showHelpText, setShowHelpText] = useState(false);
  const [open, setOpen] = useState(false);

  const requiredAlert = useMemo(() => required && !value, [required, value]);

  const showOrHideHelpText = useCallback(() => {
    const newShowState = !showHelpText;
    setShowHelpText(newShowState);
  }, [showHelpText]);

  const helpClasses = useMemo(() => {
    const helpTextClass = "text-[12px] leading-[20px]";
    if (error || requiredAlert) {
      return {
        icon: "text-ib-red-main",
        text: `text-ib-red-main ${helpTextClass}`,
      };
    }
    if (warning) {
      return {
        icon: "text-ib-yellow-warning-darkest",
        text: `text-ib-orange-warning-dark ${helpTextClass}`,
      };
    }
    if (success) {
      return {
        icon: "text-ib-green-success-dark",
        text: `text-ib-green-success-dark ${helpTextClass}`,
      };
    }
    return {
      icon: "text-ib-gray-dark-2",
      text: `text-ib-gray-dark-2 ${helpTextClass}`,
    };
  }, [error, warning, success, requiredAlert]);

  const infoMessage = useMemo(() => {
    if (requiredAlert) return translate("required");
    if (warning) {
      if (warningMessage) return warningMessage;
      return helpText;
    }
    if (error) {
      if (errorMessage) return errorMessage;
      return helpText;
    }
    if (success) {
      if (successMessage) return successMessage;
      return helpText;
    }
    if (helpText && (showHelpText || disabled || readOnly)) {
      return helpText;
    }
    return null;
  }, [
    requiredAlert,
    translate,
    warning,
    error,
    success,
    helpText,
    showHelpText,
    disabled,
    readOnly,
    warningMessage,
    errorMessage,
    successMessage,
  ]);

  const locale = language === "es" ? esLocale : enLocale;
  const display = value ? format(value, "dd/MM/yyyy", { locale }) : "";

  return (
    <div
      className={`${
        !noBottomSpace
          ? "h-[98px] min-h-[98px] max-h-[98px]"
          : "h-[65px] min-h-[65px] max-h-[65px]"
      } relative ${className}`}
    >
      <div className="flex flex-row gap-x-2 items-center mb-2">
        {label && (
          <Label label={translate(label)} id={name} required={required} />
        )}
        {helpText ? (
          <button onClick={showOrHideHelpText}>
            <Icon name="info" className="ib-gray-dark-2 size-3" />
          </button>
        ) : null}
      </div>

      <div
        className={`relative flex items-center rounded-[2px] border border-gray-300 ${
          readOnly ? "bg-ib-gray-lightest" : "bg-ib-white"
        } px-3 ${size === "medium" ? " py-[9px]" : " py-[13px]"}`}
        onClick={() => !disabled && setOpen(true)}
      >
        <input
          id={name}
          name={name}
          disabled={disabled}
          readOnly
          value={display}
          placeholder={language === "es" ? "DD/MM/AAAA" : "DD/MM/YYYY"}
          className={`w-full bg-transparent outline-none pr-16 placeholder-gray-400 text-[14px] read-only:text-ib-gray-darkest`}
        />

        {value && !disabled && !readOnly && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
            className="absolute right-9 inline-flex h-6 w-6 items-center justify-center"
            aria-label={language === "es" ? "Limpiar fecha" : "Clear date"}
            title={language === "es" ? "Limpiar" : "Clear"}
          >
            <Icon name={"closeCircle"} className={"size-4 text-ib-red-main"} />
          </button>
        )}
        <button
          ref={openRef}
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className="absolute right-2 inline-flex h-6 w-6 items-center justify-center"
          aria-label={language === "es" ? "Abrir calendario" : "Open calendar"}
          title={language === "es" ? "Abrir calendario" : "Open calendar"}
        >
          <Icon
            name="calendar"
            className={cn(
              readOnly ? "text-ib-gray-disabled" : "text-ib-red-main",
            )}
          />
        </button>
      </div>
      {!noBottomSpace ? (
        <div
          className={`h-5 flex flex-row gap-x-2 items-center ${helpClasses.icon}`}
        >
          {infoMessage ? (
            <>
              <Icon name="info" className="size-4" />
              <div className={helpClasses.text}>{infoMessage}</div>
            </>
          ) : null}
        </div>
      ) : null}
      {open && !disabled && !readOnly && (
        <div
          data-testid="calendar-to-click"
          className="absolute z-50 mt-2 rounded-lg border bg-white p-2 shadow-lg"
        >
          <DayPicker
            mode="single"
            selected={value ?? undefined}
            onSelect={(d) => {
              onChange?.(d ?? null);
              setOpen(false);
            }}
            locale={locale}
            weekStartsOn={1}
            className="rdp-red"
            navLayout="around"
            captionLayout="dropdown"
          />
        </div>
      )}
    </div>
  );
};

export default InputCalendar;
